// A drop-down that takes several choices: a field like SelectInput showing what's
// chosen, opening a list where each option toggles (owner, November 3: no rows
// of tick boxes on a form).
//
//   <FormField label="Type">{({ id }) => (
//     <MultiSelect id={id} label="Type" placeholder="Any type" values={types} options={TYPE_OPTIONS} onChange={setTypes} />
//   )}</FormField>
//
// The field is a button named "<label>: <what's chosen>"; the list is a listbox
// with aria-multiselectable, each option aria-selected. Arrow keys move, Space or
// Enter toggles, Home and End jump, Escape or Tab closes, and a click outside
// closes it. Clear empties it, which means the placeholder's default.

import { useEffect, useId, useRef, useState, type KeyboardEvent } from 'react'
import { cn } from '@/lib/format'
import { CheckIcon } from './icons'

export interface MultiSelectOption {
  value: string
  label: string
}

export interface MultiSelectProps {
  id?: string
  /** The field's name, for the button and the list. */
  label: string
  values: string[]
  options: MultiSelectOption[]
  onChange: (next: string[]) => void
  /** Shown when nothing is chosen: what that means, such as "Any type". */
  placeholder?: string
  describedBy?: string
  invalid?: boolean
  className?: string
}

const CHEVRON = (
  <svg aria-hidden viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="shrink-0 text-subtle">
    <path d="m6 9 6 6 6-6" />
  </svg>
)

/** "New, Researching", or "Verified, Needs review +2" once it's long. */
function multiSelectSummary(values: string[], options: MultiSelectOption[], placeholder: string): string {
  const chosen = options.filter((o) => values.includes(o.value)).map((o) => o.label)
  if (chosen.length === 0) return placeholder
  if (chosen.length <= 2) return chosen.join(', ')
  return `${chosen.slice(0, 2).join(', ')} +${chosen.length - 2}`
}

export function MultiSelect({ id, label, values, options, onChange, placeholder = 'Any', describedBy, invalid, className }: MultiSelectProps) {
  const [open, setOpen] = useState(false)
  const [active, setActive] = useState(0)
  const root = useRef<HTMLDivElement>(null)
  const button = useRef<HTMLButtonElement>(null)
  const optionRefs = useRef<Array<HTMLLIElement | null>>([])
  const listId = useId()
  const summary = multiSelectSummary(values, options, placeholder)

  // A click anywhere else closes it.
  useEffect(() => {
    if (!open) return undefined
    const away = (e: MouseEvent) => {
      if (root.current && !root.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', away)
    return () => document.removeEventListener('mousedown', away)
  }, [open])

  useEffect(() => {
    if (open) optionRefs.current[active]?.focus()
  }, [open, active])

  const toggle = (value: string) => onChange(values.includes(value) ? values.filter((v) => v !== value) : [...values, value])

  const show = (at = 0) => {
    setActive(Math.max(0, Math.min(at, options.length - 1)))
    setOpen(true)
  }
  const close = (refocus = true) => {
    setOpen(false)
    if (refocus) button.current?.focus()
  }

  const onButtonKey = (e: KeyboardEvent) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault()
      show(e.key === 'ArrowUp' ? options.length - 1 : 0)
    }
  }

  const onOptionKey = (e: KeyboardEvent, i: number) => {
    if (e.key === 'ArrowDown') setActive(Math.min(options.length - 1, i + 1))
    else if (e.key === 'ArrowUp') setActive(Math.max(0, i - 1))
    else if (e.key === 'Home') setActive(0)
    else if (e.key === 'End') setActive(options.length - 1)
    else if (e.key === ' ' || e.key === 'Enter') toggle(options[i].value)
    else if (e.key === 'Escape') close()
    else if (e.key === 'Tab') {
      setOpen(false)
      return
    } else return
    e.preventDefault()
  }

  return (
    <div ref={root} className={cn('relative', className)}>
      <button
        ref={button}
        id={id}
        type="button"
        aria-label={`${label}: ${summary}`}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        aria-describedby={describedBy}
        aria-invalid={invalid || undefined}
        onClick={() => (open ? close(false) : show())}
        onKeyDown={onButtonKey}
        className={cn(
          'flex h-9 w-full items-center justify-between gap-2 rounded-md border bg-raised px-3 text-left text-[13.5px] transition-colors max-md:text-base',
          'focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/25',
          invalid ? 'border-bad/70' : 'border-line-strong hover:border-subtle',
          open && 'border-accent ring-2 ring-accent/25',
        )}
      >
        <span className={cn('min-w-0 truncate', values.length === 0 ? 'text-muted' : 'text-ink')}>{summary}</span>
        {values.length > 0 ? <span className="ml-auto rounded bg-accent-soft px-1.5 text-2xs font-bold tabular-nums text-accent">{values.length}</span> : null}
        {CHEVRON}
      </button>
      {open ? (
        <div className="absolute left-0 right-0 z-30 mt-1 min-w-[12rem] overflow-hidden rounded-md border border-line bg-raised shadow-lg">
          <ul id={listId} role="listbox" aria-multiselectable="true" aria-label={label} className="max-h-72 overflow-y-auto py-1">
            {options.map((o, i) => {
              const selected = values.includes(o.value)
              return (
                <li
                  key={o.value}
                  ref={(el) => {
                    optionRefs.current[i] = el
                  }}
                  role="option"
                  aria-selected={selected}
                  tabIndex={i === active ? 0 : -1}
                  onClick={() => {
                    setActive(i)
                    toggle(o.value)
                  }}
                  onKeyDown={(e) => onOptionKey(e, i)}
                  className={cn(
                    'flex cursor-pointer items-center gap-2.5 px-3 py-2 text-[13.5px] text-ink outline-none hover:bg-hover focus-visible:bg-hover',
                    selected && 'font-semibold',
                  )}
                >
                  <span
                    aria-hidden
                    className={cn(
                      'grid h-4 w-4 shrink-0 place-items-center rounded border transition-colors',
                      selected ? 'border-accent-solid bg-accent-solid text-white' : 'border-line-strong bg-raised',
                    )}
                  >
                    {selected ? <CheckIcon size={12} /> : null}
                  </span>
                  <span className="min-w-0 break-words">{o.label}</span>
                </li>
              )
            })}
          </ul>
          <div className="flex items-center justify-between gap-2 border-t border-line px-3 py-1.5 text-xs">
            <span className="text-muted">{values.length === 0 ? placeholder : `${values.length} chosen`}</span>
            <span className="flex gap-3">
              {values.length > 0 ? (
                <button type="button" className="font-semibold text-muted hover:text-ink" onClick={() => onChange([])}>
                  Clear
                </button>
              ) : null}
              <button type="button" className="font-semibold text-accent hover:underline" onClick={() => close()}>
                Done
              </button>
            </span>
          </div>
        </div>
      ) : null}
    </div>
  )
}
